import {InterventionTypeDTO, type ProgramTileDTO} from '@/ts/types/dto/today-dto';
import type {ProgramCatalogue, TileProgram} from '@/ts/types/component/today-page';

/** The two groups the design files the catalogue into, keyed by what the payload calls them. */
export const CATALOGUE_BY_TYPE: Record<InterventionTypeDTO, ProgramCatalogue> = {
  [InterventionTypeDTO.MEDICAL]: 'medical',
  [InterventionTypeDTO.NON_MEDICAL]: 'prevention',
};

/**
 * Translates the program tile's slice of `GET /me/today` into the rows the tile draws.
 *
 * The list is the programs the user is in, under way or finished, and not the catalogue their
 * licence grants: the tile is about where they stand, and a program never joined has nowhere to
 * stand. Whether a group has anything left to browse is a separate question, answered by
 * `availableCatalogues` below rather than by these rows.
 *
 * A program the CMS never categorised does not reach here at all: the backend leaves it out of the
 * payload rather than guessing a group for it, so nothing here has to invent one either.
 *
 * Pure and clock-free: `completed` is the payload's own flag, not a progress value compared against
 * a threshold, which is what the interventions endpoint forced.
 */
export function mapTodayPrograms(dto: ProgramTileDTO): TileProgram[] {
  return dto.programs.map((program) => ({
    interventionId: program.interventionId,
    name: program.name,
    catalogue: CATALOGUE_BY_TYPE[program.type],
    progress: program.progress,
    completed: program.completed,
    image: program.image,
  }));
}

/**
 * Which of the two groups the tile may invite the user into: the ones holding a program this user
 * could start.
 *
 * Narrower than "their licence covers it", and deliberately so, because inviting someone to browse
 * a group is only honest when there is something there for them: a group whose only program they
 * already finished does not qualify, nor does one whose programs are discontinued, in maintenance
 * or still in preview. A finished program of their own still shows as a row — the tile draws what
 * they have whatever this says.
 *
 * Read off the payload rather than worked out from the rows. Those are only the programs the user
 * is in, so a licence that offers prevention to someone who joined none of it looks, from the rows,
 * exactly like a licence with no prevention in it — and the tile would then hide the very
 * invitation that user needs.
 *
 * Both groups are answered the same way on purpose. The medical block used to be drawn
 * unconditionally, which reads as an oversight next to a prevention block that is not, and sends a
 * licence holding only prevention to a page with nothing on it.
 *
 * WITHOUT THE FIELD, which is a backend not yet deployed rather than a user state, each group keeps
 * exactly the answer it had before it existed: prevention worked out from the rows, medical always
 * yes. So the tile behaves as it does today until the payload can tell it better.
 */
export function availableCatalogues(dto: ProgramTileDTO): Record<ProgramCatalogue, boolean> {
  const declared = dto.availableCatalogues;
  if (!declared) {
    return {
      medical: true,
      prevention: dto.programs.some((program) => program.type === InterventionTypeDTO.NON_MEDICAL),
    };
  }

  return {
    medical: declared.includes(InterventionTypeDTO.MEDICAL),
    prevention: declared.includes(InterventionTypeDTO.NON_MEDICAL),
  };
}
